import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { processSteps, testimonials } from "@/data/content";
import { Reveal } from "@/components/site/motion";
import { SectionHeading } from "@/components/site/Blocks";
import { http } from "@/lib/api";

export const Process = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 80%", "end 60%"] });
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);
  return (
    <section ref={ref} className="section-lux bg-ivory" data-testid="home-process">
      <div className="container-lux">
        <SectionHeading eyebrow="The Journey" lines={["Four movements,", "composed with care."]} />
        <div className="relative mt-24 md:mt-32">
          <div className="absolute left-0 right-0 top-0 hidden h-px bg-navy/10 lg:block" />
          <motion.div style={{ scaleX }} className="absolute left-0 right-0 top-0 hidden h-px origin-left bg-gold lg:block" />
          <ol className="grid gap-16 lg:grid-cols-4 lg:gap-14">
            {processSteps.map((s, i) => (
              <Reveal as="li" key={s.n} delay={i * 0.12} className="relative border-t border-gold/40 pt-10 lg:border-t-0 lg:pt-14" data-testid={`process-step-${i}`}>
                <span className="font-serif text-7xl font-light italic leading-none text-gold-deep md:text-8xl">{s.n}</span>
                <h3 className="mt-10 font-serif text-4xl font-light text-navy">{s.title}</h3>
                <p className="mt-5 max-w-xs text-[16px] font-light leading-relaxed text-charcoal-soft">{s.text}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

export const Testimonials = () => {
  const [i, setI] = useState(0);
  const n = testimonials.length;
  useEffect(() => {
    const t = setInterval(() => setI((x) => (x + 1) % n), 7000);
    return () => clearInterval(t);
  }, [i, n]);
  return (
    <section className="section-lux relative overflow-hidden bg-ivory-100" data-testid="home-testimonials">
      <div className="container-lux grid gap-20 lg:grid-cols-[0.7fr_1.3fr]">
        <div>
          <SectionHeading eyebrow="Client Voices" lines={["In their", "own words."]} />
          <div className="mt-12 flex items-center gap-4">
            <button type="button" onClick={() => setI((i - 1 + n) % n)} aria-label="Previous testimonial" data-testid="testimonial-prev-btn" className="flex h-14 w-14 items-center justify-center rounded-full border border-navy/25 text-navy transition-colors duration-500 hover:border-navy hover:bg-navy hover:text-ivory"><ArrowLeft className="h-5 w-5" /></button>
            <button type="button" onClick={() => setI((i + 1) % n)} aria-label="Next testimonial" data-testid="testimonial-next-btn" className="flex h-14 w-14 items-center justify-center rounded-full border border-navy/25 text-navy transition-colors duration-500 hover:border-navy hover:bg-navy hover:text-ivory"><ArrowRight className="h-5 w-5" /></button>
            <span className="ml-4 font-sans text-[12px] tracking-[0.25em] text-charcoal-soft" data-testid="testimonial-counter">0{i + 1} / 0{n}</span>
          </div>
        </div>
        <div className="grid">
          {testimonials.map((t, k) => (
            <motion.figure key={k} style={{ gridArea: "1 / 1" }} initial={false} animate={{ opacity: k === i ? 1 : 0, y: k === i ? 0 : 20 }} transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }} aria-hidden={k !== i} className={k === i ? "" : "pointer-events-none"} data-testid={`testimonial-slide-${k}`}>
              <span className="font-serif text-8xl leading-none text-gold">“</span>
              <blockquote className="-mt-6 font-serif text-[2rem] font-light leading-[1.25] text-navy md:text-5xl lg:text-[3.6rem]">{t.quote}</blockquote>
              <figcaption className="mt-10 flex items-center gap-4">
                <span className="h-px w-10 bg-gold" />
                <span><span className="block font-sans text-[13px] font-semibold uppercase tracking-[0.2em] text-navy">{t.name}</span><span className="text-[13px] text-charcoal-soft">{t.role}</span></span>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};

export const PostCard = ({ p, i = 0 }) => (
  <Reveal delay={(i % 3) * 0.1}>
    <Link to={`/insights/${p.slug}`} className="group block" data-testid={`insight-card-${p.slug}`}>
      <div className="aspect-[4/3] overflow-hidden bg-navy">
        <img src={p.cover || "/img/london-aerial.jpg"} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1.4s] ease-[cubic-bezier(.2,.7,.2,1)] group-hover:scale-105" />
      </div>
      <div className="mt-8 flex items-center gap-3 font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-deep">
        <span>{p.tag || p.category}</span><span className="h-px w-6 bg-gold" /><span className="text-charcoal-soft">{p.read_time} min read</span>
      </div>
      <h3 className="mt-5 font-serif text-[2.1rem] font-light leading-[1.1] text-navy transition-colors group-hover:text-gold-deep">{p.title}</h3>
      <p className="mt-4 line-clamp-2 text-[16px] font-light leading-relaxed text-charcoal-soft">{p.excerpt}</p>
    </Link>
  </Reveal>
);

export const InsightsPreview = () => {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    http.get("/posts", { params: { limit: 3 } }).then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch(() => setPosts([]));
  }, []);
  if (!posts.length) return null;
  return (
    <section className="section-lux bg-ivory" data-testid="home-insights">
      <div className="container-lux">
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <SectionHeading eyebrow="Insights" lines={["Perspective from", "the private desk."]} />
          <Link to="/insights" className="link-lux font-sans text-[12px] font-semibold uppercase tracking-[0.22em] text-navy" data-testid="insights-view-all-link">All insights <ArrowUpRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-24 grid gap-20 md:grid-cols-3 md:gap-12">
          {posts.map((p, i) => <PostCard key={p.slug} p={p} i={i} />)}
        </div>
      </div>
    </section>
  );
};
